import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GripVertical, Loader2 } from 'lucide-react';
import { Reorder, useDragControls } from 'framer-motion';
import { ExchangeTile } from './ExchangeTile';
import { ExchangeSetupDrawer } from './ExchangeSetupDrawer';
import { ExchangeSetupDialog } from './ExchangeSetupDialog';
import {
  useAvailableExchanges,
  useUserExchanges,
  useRemoveExchange,
  useRefreshBalance,
  useReorderExchanges,
} from '@/hooks/useExchanges';
import { useIsDesktop } from '@/hooks/useMediaQuery';
import { toast } from 'sonner';
import { trackEvent } from '@/utils/analytics';
import type { UserExchange } from '@crypto-trading/common/browser';

interface ExchangeConnectListProps {
  userId: string;
  compact?: boolean;
}

interface SortableExchangeRowProps {
  exchange: {
    code: string;
    name: string;
  };
  userExchange?: UserExchange;
  priorityRank: number;
  compact: boolean;
  refreshingCode: string | null;
  onRemove: (exchangeCode: string, exchangeName: string) => void;
  onRefresh: (exchangeCode: string) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}

const SortableExchangeRow: React.FC<SortableExchangeRowProps> = ({
  exchange,
  userExchange,
  priorityRank,
  compact,
  refreshingCode,
  onRemove,
  onRefresh,
  onDragStart,
  onDragEnd,
}) => {
  const controls = useDragControls();

  return (
    <Reorder.Item
      value={exchange.code}
      dragListener={false}
      dragControls={controls}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className="touch-none"
    >
      <ExchangeTile
        code={exchange.code}
        name={exchange.name}
        isConnected
        freeBalance={userExchange?.balance?.free}
        usedBalance={userExchange?.balance?.locked}
        errorMessage={userExchange?.balance?.error}
        isBalancePending={!!userExchange?.balance?.pending}
        onRemove={() => onRemove(exchange.code, exchange.name)}
        onRefresh={() => onRefresh(exchange.code)}
        isRefreshing={refreshingCode === exchange.code}
        compact={compact}
        priorityRank={priorityRank}
        showDragHandle
        onDragHandlePointerDown={(event) => controls.start(event)}
      />
    </Reorder.Item>
  );
};

export const ExchangeConnectList: React.FC<ExchangeConnectListProps> = ({
  userId,
  compact = false,
}) => {
  const isDesktop = useIsDesktop();
  const [selectedExchange, setSelectedExchange] = useState<{
    code: string;
    name: string;
  } | null>(null);

  const [refreshingCode, setRefreshingCode] = useState<string | null>(null);
  const [orderedCodes, setOrderedCodes] = useState<string[]>([]);
  const orderedCodesRef = useRef<string[]>([]);

  const { data: availableExchanges = [], isLoading: loadingAvailable, error: availableError } = useAvailableExchanges();
  const { data: userExchanges = [], isLoading: loadingUser, error: userError, refetch } = useUserExchanges(userId, true);
  const removeExchangeMutation = useRemoveExchange();
  const refreshBalanceMutation = useRefreshBalance();
  const reorderExchangesMutation = useReorderExchanges();

  const isLoading = loadingAvailable || loadingUser;
  const error = availableError || userError;

  const userExchangeByCode = useMemo(() => {
    const map = new Map<string, UserExchange>();
    for (const exchange of userExchanges) {
      map.set(exchange.code, exchange);
    }
    return map;
  }, [userExchanges]);

  const connectedFromServer = useMemo(
    () =>
      [...userExchanges].sort(
        (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
      ),
    [userExchanges]
  );

  useEffect(() => {
    const next = connectedFromServer.map((exchange) => exchange.code);
    setOrderedCodes(next);
    orderedCodesRef.current = next;
  }, [connectedFromServer]);

  useEffect(() => {
    const preventTelegramSwipe = (event: TouchEvent) => {
      if (!document.documentElement.classList.contains("tg-dragging")) return;
      event.preventDefault();
    };
    document.addEventListener("touchmove", preventTelegramSwipe, {
      passive: false,
    });
    return () => {
      document.removeEventListener("touchmove", preventTelegramSwipe);
      document.documentElement.classList.remove("tg-dragging");
    };
  }, []);

  const connectedExchanges = useMemo(() => {
    const availableByCode = new Map(
      availableExchanges.map((exchange) => [exchange.exchangeCode, exchange])
    );
    return orderedCodes
      .filter((code) => userExchangeByCode.has(code))
      .map((code) => {
        const available = availableByCode.get(code);
        const userExchange = userExchangeByCode.get(code);
        return {
          code,
          name: available?.exchangeName ?? userExchange?.name ?? code,
        };
      });
  }, [availableExchanges, orderedCodes, userExchangeByCode]);

  const disconnectedExchanges = useMemo(
    () =>
      availableExchanges.filter(
        (exchange) => !userExchangeByCode.has(exchange.exchangeCode)
      ),
    [availableExchanges, userExchangeByCode]
  );

  const handleRefresh = async (exchangeCode: string) => {
    if (refreshingCode) return;
    setRefreshingCode(exchangeCode);
    try {
      await refreshBalanceMutation.mutateAsync({ userId, exchangeCode });
      toast.success('Balance updated');
    } catch (err: unknown) {
      const throttleErr = err as { status?: number; retryAfterSeconds?: number };
      if (throttleErr?.status === 429 && typeof throttleErr.retryAfterSeconds === 'number') {
        const minutesLeft = Math.ceil(throttleErr.retryAfterSeconds / 60);
        toast.error(`Try again in ${minutesLeft} min`);
      } else {
        toast.error('Failed to refresh balance');
      }
    } finally {
      setRefreshingCode(null);
    }
  };

  const handleConnect = (exchangeCode: string, exchangeName: string) => {
    trackEvent('exchange_connect_click', {
      userId,
      exchangeCode,
      exchangeName,
      source: 'settings',
    });
    setSelectedExchange({ code: exchangeCode, name: exchangeName });
  };

  const handleRemove = async (exchangeCode: string, exchangeName: string) => {
    try {
      await removeExchangeMutation.mutateAsync({
        userId,
        exchangeCode,
      });
      toast.success(`${exchangeName} removed successfully!`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to remove exchange';
      toast.error(errorMessage);
    }
  };

  const handleReorder = (nextCodes: string[]) => {
    setOrderedCodes(nextCodes);
    orderedCodesRef.current = nextCodes;
  };

  const persistOrder = async () => {
    document.documentElement.classList.remove('tg-dragging');
    const codes = orderedCodesRef.current;
    const serverOrder = connectedFromServer.map((exchange) => exchange.code);
    if (
      codes.length < 2 ||
      codes.length !== serverOrder.length ||
      codes.every((code, index) => code === serverOrder[index])
    ) {
      return;
    }

    try {
      await reorderExchangesMutation.mutateAsync({
        userId,
        exchangeCodes: codes,
      });
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to update autotrade priority'
      );
      const rollback = connectedFromServer.map((exchange) => exchange.code);
      setOrderedCodes(rollback);
      orderedCodesRef.current = rollback;
    }
  };

  useEffect(() => {
    if (selectedExchange) {
      trackEvent('exchange_connect_modal_open', {
        userId,
        exchangeCode: selectedExchange.code,
        exchangeName: selectedExchange.name,
        source: 'settings',
      });
    }
  }, [selectedExchange]);

  if (isLoading) {
    return (
      <div className="bg-[#111217] border border-gray-800 rounded-3xl p-8 text-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent mx-auto mb-4" />
        <p className="text-text-primary">Loading exchanges...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#111217] border border-red-500/30 rounded-3xl p-6 text-center space-y-3">
        <div className="w-14 h-14 bg-red-500/10 rounded-full flex items-center justify-center mx-auto">
          <span className="text-red-400 text-2xl">⚠️</span>
        </div>
        <h2 className="text-lg font-bold text-text-primary">Error Loading Exchanges</h2>
        <p className="text-text-secondary text-sm">
          {error instanceof Error ? error.message : 'Failed to load exchanges. Please try again.'}
        </p>
        <button
          onClick={() => refetch()}
          className="bg-accent text-text-primary px-6 py-2 rounded-lg hover:bg-accent/80 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  const connectedCount = connectedExchanges.length;
  const totalCount = availableExchanges.length;

  return (
    <div className="bg-[#111217] border border-gray-800 rounded-3xl p-4 space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-text-secondary">Exchanges</p>
          <h2 className="text-lg font-semibold text-text-primary">Connected Exchanges</h2>
        </div>
        <span className="text-accent text-xs font-semibold">
          {connectedCount} / {totalCount} connected
        </span>
      </div>

      {connectedCount > 0 && (
        <div className="flex items-start gap-2 rounded-xl border border-gray-800 bg-black/30 px-3 py-2">
          <GripVertical className="w-3.5 h-3.5 text-text-secondary mt-0.5 shrink-0" />
          <p className="text-xs text-text-secondary leading-relaxed">
            Drag exchanges to change autotrade priority. The top exchange is tried
            first; the ones below are fallbacks, in that order.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {connectedExchanges.length > 0 && (
          <Reorder.Group
            axis="y"
            values={orderedCodes}
            onReorder={handleReorder}
            className="space-y-4 list-none p-0 m-0"
          >
            {connectedExchanges.map((exchange, index) => (
              <SortableExchangeRow
                key={exchange.code}
                exchange={exchange}
                userExchange={userExchangeByCode.get(exchange.code)}
                priorityRank={index + 1}
                compact={compact}
                refreshingCode={refreshingCode}
                onRemove={handleRemove}
                onRefresh={handleRefresh}
                onDragStart={() => document.documentElement.classList.add('tg-dragging')}
                onDragEnd={persistOrder}
              />
            ))}
          </Reorder.Group>
        )}

        {disconnectedExchanges.map((exchange) => (
          <ExchangeTile
            key={exchange.exchangeCode}
            code={exchange.exchangeCode}
            name={exchange.exchangeName}
            isConnected={false}
            onConnect={() => handleConnect(exchange.exchangeCode, exchange.exchangeName)}
            compact={compact}
          />
        ))}
      </div>

      {availableExchanges.length === 0 && (
        <div className="text-center py-12">
          <p className="text-text-secondary">No exchanges available</p>
        </div>
      )}

      {selectedExchange && (
        isDesktop ? (
          <ExchangeSetupDialog
            open={!!selectedExchange}
            onOpenChange={(open) => !open && setSelectedExchange(null)}
            exchangeCode={selectedExchange.code}
            exchangeName={selectedExchange.name}
            userId={userId}
          />
        ) : (
          <ExchangeSetupDrawer
            open={!!selectedExchange}
            onOpenChange={(open) => !open && setSelectedExchange(null)}
            exchangeCode={selectedExchange.code}
            exchangeName={selectedExchange.name}
            userId={userId}
          />
        )
      )}
    </div>
  );
};
